import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Portal,
} from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { cn } from '@/utils/classname';

export interface RowAction {
  /** Display label for the action */
  label: string;
  /** Icon element to display before the label */
  icon: React.ReactNode;
  /** Click handler for the action */
  onClick: () => void;
  /** Visual variant: 'danger' applies critical styling */
  variant?: 'default' | 'danger';
  /** Optional custom test id (defaults to generated from testIdPrefix-actions-itemKey-label) */
  testId?: string;
}

export interface RowActionsMenuProps {
  /** Array of action items to display in the menu */
  actions: RowAction[];
  /** Prefix for data-testid attributes (e.g., 'webhook', 'email') */
  testIdPrefix: string;
  /** Unique identifier for the row item (used in test ids) */
  itemKey: string;
  /** Accessible label for the menu button */
  ariaLabel?: string;
}

/**
 * Reusable ellipsis menu for table row actions.
 *
 * Provides a standardized three-dot menu with configurable actions,
 * consistent styling, and proper accessibility attributes.
 *
 * Features:
 * - Headless UI Menu with Portal rendering
 * - Support for default and danger action variants
 * - Consistent test id generation pattern
 * - Focus/hover state styling
 *
 * Usage:
 * ```tsx
 * <RowActionsMenu
 *   actions={[
 *     { label: 'Edit', icon: <PencilIcon className="size-4" />, onClick: handleEdit },
 *     { label: 'Delete', icon: <TrashIcon className="size-4" />, onClick: handleDelete, variant: 'danger' },
 *   ]}
 *   testIdPrefix="webhook"
 *   itemKey={item.key}
 * />
 * ```
 */
export function RowActionsMenu({
  actions,
  testIdPrefix,
  itemKey,
  ariaLabel = 'Row actions',
}: RowActionsMenuProps) {
  return (
    <Menu as="div" className="relative">
      <MenuButton
        className={cn(
          'rounded-md p-1 transition-colors',
          'hover:bg-chariot-background-secondary',
          'focus:ring-chariot-brand-primary focus:ring-2 focus:outline-none'
        )}
        data-testid={`${testIdPrefix}-actions-${itemKey}`}
        aria-label={ariaLabel}
      >
        <EllipsisVerticalIcon className="text-chariot-text-secondary size-5" />
      </MenuButton>

      <Portal>
        <MenuItems
          anchor="bottom end"
          className={cn(
            'z-50 mt-1 w-32',
            'bg-chariot-background-primary rounded-lg shadow-lg',
            'border-chariot-stroke border',
            'focus:outline-none'
          )}
        >
          {actions.map((action, index) => (
            <MenuItem key={index}>
              {({ focus }) => (
                <button
                  onClick={action.onClick}
                  className={cn(
                    'flex w-full items-center gap-2 px-4 py-2 text-left text-sm',
                    focus && 'bg-chariot-background-secondary',
                    action.variant === 'danger'
                      ? 'text-chariot-stroke-critical'
                      : 'text-chariot-text-primary'
                  )}
                  data-testid={
                    action.testId ||
                    `${testIdPrefix}-actions-${itemKey}-${action.label.toLowerCase()}`
                  }
                >
                  {action.icon}
                  {action.label}
                </button>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Portal>
    </Menu>
  );
}
